import {
  revenueEntryCreateSchema,
  revenueEntryUpdateSchema,
  yesterdayInTimezone,
} from '@mm/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api, ApiError, type QueryParams } from '../../api/client';
import { AppIcon } from '../../components/AppIcon';
import { ExportButton } from '../../components/ExportButton';
import { DateRangeFilter, useDateFilter } from '../../components/DateRangeFilter';
import { Button } from '../../components/ui/Button';
import { Badge, Card, PageHeader } from '../../components/ui/display';
import { Input } from '../../components/ui/inputs';
import { ConfirmDialog, Modal } from '../../components/ui/Modal';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { useToast } from '../../components/ui/Toast';
import { type FieldDef, type FormValues, ResourceForm } from '../../forms/ResourceForm';
import { useDebounce } from '../../hooks/useDebounce';
import { DataTable } from '../../tables/DataTable';
import type { RevenueEntry, RevenueSummary } from '../../types/models';
import { formatDate, formatDecimal, formatMoney } from '../../utils/format';
import { useAuth } from '../auth/AuthProvider';
import { Stat } from '../marketing/MarketingPage';
import { sources } from '../masters/sources';

const createFields: FieldDef[] = [
  { name: 'transactionDate', label: 'Date', type: 'date', required: true },
  { name: 'appId', label: 'App', type: 'select', required: true, remote: sources.app() },
  {
    name: 'revenueAccountId',
    label: 'Revenue account',
    type: 'select',
    required: true,
    help: 'Accounts currently assigned to the app',
    remote: (v) =>
      sources.revenueAccount(
        v.appId ? { status: 'ACTIVE', appId: String(v.appId) } : { status: 'ACTIVE' },
      ),
  },
  { name: 'revenueAmount', label: 'Revenue (account currency)', type: 'decimal', required: true },
  {
    name: 'exchangeRate',
    label: 'Exchange rate (₹)',
    type: 'decimal',
    help: "Leave empty to use the month's configured rate; a value here is saved as a manual rate",
  },
  { name: 'notes', label: 'Notes', type: 'textarea', wide: true },
];

const editFields: FieldDef[] = [
  { name: 'revenueAmount', label: 'Revenue (account currency)', type: 'decimal', required: true },
  {
    name: 'exchangeRate',
    label: 'Exchange rate (₹)',
    type: 'decimal',
    help: 'Changing the rate marks this entry as a manual rate',
  },
  { name: 'notes', label: 'Notes', type: 'textarea', wide: true },
];

type Editing = { mode: 'create' } | { mode: 'edit'; row: RevenueEntry } | null;

export function RevenueEntriesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [range, setRange] = useDateFilter('thisMonth');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('-transactionDate');
  const [editing, setEditing] = useState<Editing>(null);
  const [deleting, setDeleting] = useState<RevenueEntry | null>(null);
  const debounced = useDebounce(search);
  const canWrite = can('transactions.write');

  const filterParams: QueryParams = {
    startDate: range.startDate,
    endDate: range.endDate,
    ...filters,
  };
  const list = useQuery({
    queryKey: ['revenue', 'list', filterParams, page, sort, debounced],
    queryFn: () =>
      api.list<RevenueEntry>('/revenue-entries', {
        ...filterParams,
        page,
        pageSize: 50,
        sort,
        search: debounced,
      }),
    placeholderData: keepPreviousData,
  });
  const summary = useQuery({
    queryKey: ['revenue', 'summary', filterParams],
    queryFn: () => api.get<RevenueSummary>('/revenue-entries/summary', filterParams),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    for (const key of ['revenue', 'dashboard', 'morning-entry'])
      void queryClient.invalidateQueries({ queryKey: [key] });
  };
  const remove = useMutation({
    mutationFn: (row: RevenueEntry) => api.delete(`/revenue-entries/${row.id}`),
    onSuccess: () => {
      toast.success('Entry deleted');
      setDeleting(null);
      invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Delete failed');
      setDeleting(null);
    },
  });
  const setFilter = (name: string, value: string) => {
    setFilters((f) => ({ ...f, [name]: value }));
    setPage(1);
  };

  const s = summary.data;
  const editRow = editing?.mode === 'edit' ? editing.row : null;

  return (
    <>
      <PageHeader
        title="Revenue entries"
        description="Daily revenue per account in its own currency. Each entry keeps the exchange rate it was saved with."
        actions={
          <>
            <ExportButton
              report="revenue-entries"
              params={{ ...filterParams, search: debounced }}
            />
            <DateRangeFilter
              value={range}
              onChange={(v) => {
                setRange(v);
                setPage(1);
              }}
            />
            {canWrite && (
              <Button
                icon={<Plus className="size-4" />}
                onClick={() => setEditing({ mode: 'create' })}
              >
                Add entry
              </Button>
            )}
          </>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Revenue (INR)" value={formatMoney(s?.revenueInr)} tone="profit" />
        {(s?.byCurrency ?? [])
          .filter((c) => c.currencyCode !== 'INR')
          .slice(0, 2)
          .map((c) => (
            <Stat
              key={c.currencyCode}
              label={`In ${c.currencyCode}`}
              value={formatMoney(c.revenueAmount, c.currencyCode)}
              sub={`= ${formatMoney(c.revenueInr)}`}
            />
          ))}
        <Stat label="Entries" value={s ? String(s.count) : '—'} />
      </div>

      <Card bodyClassName="p-0">
        <div className="grid grid-cols-1 gap-3 border-b border-slate-100 p-4 sm:grid-cols-2 xl:grid-cols-5">
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search account, app, notes…"
              className="pl-9"
              aria-label="Search"
            />
          </div>
          <SearchableSelect
            ariaLabel="App"
            placeholder="All apps"
            clearable
            value={filters.appId ?? ''}
            remote={sources.app({})}
            onChange={(v) => setFilter('appId', v)}
          />
          <SearchableSelect
            ariaLabel="Source"
            placeholder="All sources"
            clearable
            value={filters.revenueSourceId ?? ''}
            remote={sources.revenueSource({})}
            onChange={(v) => setFilter('revenueSourceId', v)}
          />
          <SearchableSelect
            ariaLabel="Currency"
            placeholder="All currencies"
            clearable
            value={filters.currencyCode ?? ''}
            remote={sources.currency({})}
            onChange={(v) => setFilter('currencyCode', v)}
          />
          <SearchableSelect
            ariaLabel="Rate type"
            placeholder="Monthly and manual rates"
            clearable
            value={filters.exchangeRateSource ?? ''}
            options={[
              { value: 'MONTHLY', label: 'Monthly rate' },
              { value: 'MANUAL', label: 'Manual rate' },
            ]}
            onChange={(v) => setFilter('exchangeRateSource', v)}
          />
        </div>
        <DataTable<RevenueEntry>
          tableId="revenue-entries"
          rows={list.data?.data}
          rowKey={(r) => r.id}
          loading={list.isLoading}
          meta={list.data?.meta}
          onPageChange={setPage}
          sort={sort}
          onSortChange={setSort}
          onRowClick={canWrite ? (row) => setEditing({ mode: 'edit', row }) : undefined}
          columns={[
            {
              key: 'transactionDate',
              header: 'Date',
              sortKey: 'transactionDate',
              render: (r) => (
                <span className="whitespace-nowrap">{formatDate(r.transactionDate)}</span>
              ),
            },
            {
              key: 'app',
              header: 'App',
              sortKey: 'app',
              render: (r) => (
                <span className="flex items-center gap-2">
                  <AppIcon src={r.app.iconUrl} name={r.app.name} size={22} />
                  {r.app.name}
                </span>
              ),
            },
            {
              key: 'source',
              header: 'Source',
              sortKey: 'source',
              render: (r) => r.revenueSource.name,
            },
            {
              key: 'account',
              header: 'Account',
              sortKey: 'account',
              render: (r) => r.revenueAccount.accountName,
            },
            {
              key: 'revenueAmount',
              header: 'Revenue',
              align: 'right',
              sortKey: 'revenueAmount',
              render: (r) => formatMoney(r.revenueAmount, r.currencyCode),
            },
            {
              key: 'rate',
              header: 'Rate',
              align: 'right',
              render: (r) => (
                <span className="inline-flex items-center gap-1.5">
                  {r.currencyCode === 'INR' ? '—' : `₹${formatDecimal(r.exchangeRate)}`}
                  {r.exchangeRateSource === 'MANUAL' && <Badge tone="amber">manual</Badge>}
                </span>
              ),
            },
            {
              key: 'revenueInr',
              header: 'INR',
              align: 'right',
              sortKey: 'revenueInr',
              render: (r) => (
                <span className="font-medium text-slate-900">{formatMoney(r.revenueInr)}</span>
              ),
            },
            ...(canWrite
              ? [
                  {
                    key: '__actions',
                    header: <span className="sr-only">Actions</span>,
                    align: 'right' as const,
                    render: (r: RevenueEntry) => (
                      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                          aria-label="Edit entry"
                          onClick={() => setEditing({ mode: 'edit', row: r })}
                        >
                          <Pencil className="size-4" />
                        </button>
                        <button
                          type="button"
                          className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                          aria-label="Delete entry"
                          onClick={() => setDeleting(r)}
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </div>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        size="sm"
        title={
          editRow
            ? `Edit revenue — ${editRow.revenueAccount.accountName}, ${formatDate(editRow.transactionDate)}`
            : 'Add revenue entry'
        }
        description={
          editRow
            ? `${editRow.currencyCode} · saved with rate ₹${formatDecimal(editRow.exchangeRate)} (${editRow.exchangeRateSource.toLowerCase()})`
            : undefined
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button type="submit" form="revenue-form">
              {editRow ? 'Save changes' : 'Create'}
            </Button>
          </>
        }
      >
        {editing && (
          <ResourceForm
            key={editRow?.id ?? 'new'}
            formId="revenue-form"
            fields={(editRow
              ? editFields.filter(
                  (f) => f.name !== 'exchangeRate' || editRow.currencyCode !== 'INR',
                )
              : createFields
            ).filter((f) => f.name !== 'exchangeRate' || can('fx.override'))}
            schema={editRow ? revenueEntryUpdateSchema : revenueEntryCreateSchema}
            isEdit={!!editRow}
            lookups={undefined}
            defaultValues={
              editRow
                ? {
                    revenueAmount: String(Number(editRow.revenueAmount)),
                    exchangeRate: String(Number(editRow.exchangeRate)),
                    notes: editRow.notes ?? '',
                  }
                : {
                    transactionDate: yesterdayInTimezone(),
                    appId: '',
                    revenueAccountId: '',
                    revenueAmount: '',
                    exchangeRate: '',
                    notes: '',
                  }
            }
            onSubmit={async (values: FormValues) => {
              if (editRow) {
                const body: FormValues = { ...values, updatedAt: editRow.updatedAt };
                // Only send the rate if it was actually changed (otherwise it stays as saved).
                if (values.exchangeRate === String(Number(editRow.exchangeRate)))
                  delete body.exchangeRate;
                await api.put(`/revenue-entries/${editRow.id}`, body);
                toast.success('Entry updated');
              } else {
                await api.post('/revenue-entries', values);
                toast.success('Entry created');
              }
              setEditing(null);
              invalidate();
            }}
          />
        )}
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title="Delete this entry?"
        message={
          deleting &&
          `${deleting.revenueAccount.accountName} on ${formatDate(deleting.transactionDate)} (${formatMoney(deleting.revenueAmount, deleting.currencyCode)}). The day will show as missing again until re-entered.`
        }
        confirmLabel="Delete"
        loading={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting)}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}
